import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import GallerySection from '../components/GallerySection';
import TestimonialsSection from '../components/TestimonialsSection';
import { imageUrl } from '../lib/images';

export default function FamiliefotograferingPage() {
  return (
    <>
      <SEO
        title="Familiefotografering"
        description="Naturlig og afslappet familiefotografering i Kolding og omegn. Hjemme hos jer selv eller på en smuk udendørs lokation. Uden stive smil."
      />

      {/* Hero Section (Cinematic Dark Sunset / Forest) */}
      <section className="relative min-h-[75vh] flex items-center justify-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('/_assets/482211624-1118484263628353-76151960718850419-n.jpg', 'width=1920,quality=85,format=auto')}
            alt="Smilende pige ved fotografering med Lunde Media"
            className="w-full h-full object-cover filter brightness-[0.6] contrast-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0D] via-[#0C0C0D]/30 to-[#0C0C0D]/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 text-center space-y-6">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-medium text-[#F4EFEA] tracking-tight leading-tight max-w-4xl mx-auto">
            Ægte relationer & naturlige øjeblikke
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-zinc-300 font-sans max-w-2xl mx-auto leading-relaxed font-normal">
            Børn vokser hurtigt, og tiden flyver afsted. Få foreviget jeres familie, som I virkelig er – med masser af kram, leg og naturlig kærlighed.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt?ydelse=familie"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Book familiefotografering
            </Link>
            <a
              href="#galleri"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              Se familiegalleriet
            </a>
          </div>
        </div>
      </section>

      {/* Light Editorial Story / Location options */}
      <section className="py-20 md:py-28 bg-white text-[#141416] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-5 aspect-[4/5] rounded-none overflow-hidden border border-zinc-200 shadow-lg">
              <img
                src={imageUrl('/_assets/482221143-1118484233628356-4166053169173591826-n.jpg', 'width=800,quality=85,format=auto')}
                alt="Familiehygge og nærvær med børn"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="md:col-span-7 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-serif text-[#141416] leading-tight">
                Ingen stive atelier-kulisser eller tvungne smil
              </h2>
              <div className="space-y-4 text-[#3A3836] text-sm md:text-base leading-relaxed font-sans">
                <p>
                  En familiefotografering hos Lunde Media foregår altid på jeres præmisser. Børn skal have lov til at lege, løbe, kigge på biller og være præcis som de er.
                </p>
                <p>
                  Fotograferingen kan foregå <strong>hjemme på jeres private adresse</strong>, hvor alle føler sig trygge i vante omgivelser, eller <strong>ude på en udvalgt lokation</strong> såsom i skoven, ved stranden eller i en smuk park i gyldent eftermiddagslys.
                </p>
                <p>
                  Jeg guider jer nænsomt gennem en hyggelig gåtur eller leg, så I glemmer kameraet og bare nyder hinandens selskab.
                </p>
              </div>

              <div className="space-y-3 pt-4 border-t border-zinc-200 text-sm">
                <div>
                  <span className="font-semibold text-[#141416]">Hjemme hos jer selv: </span>
                  <span className="text-[#575450]">Perfekt til spædbørn, hyggestunder i haven og familiens eget miljø.</span>
                </div>
                <div>
                  <span className="font-semibold text-[#141416]">I naturen: </span>
                  <span className="text-[#575450]">Strand, skov, klitter eller åbne marker med smukt naturligt lys.</span>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt?emne=familie"
                  className="cta-btn inline-flex items-center gap-2 px-6 py-3 rounded-none bg-[#141416] text-white hover:bg-black font-serif text-sm tracking-wide transition-colors"
                >
                  <span>Book familiefotografering</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery from CMS (Dark Photographic Gallery) */}
      <div id="galleri">
        <GallerySection
          slug="familiefotografering"
          title="Familiegalleri"
          subtitle="Minder for livet · Relationer & Nærvær"
          className="bg-[#0C0C0D]"
        />
      </div>

      {/* Light Good-to-know Tips Section */}
      <section className="py-20 md:py-28 bg-white text-[#141416] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8 space-y-12">
          <div className="text-center space-y-3">
            <h2 className="text-3xl sm:text-4xl font-serif text-[#141416]">
              Sådan gør vi klar til en god fotografering
            </h2>
            <p className="text-[#575450] text-sm md:text-base max-w-xl mx-auto">
              Et par enkle forberedelser skaber de bedste forudsætninger for rolige, smukke billeder.
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-6 sm:p-8 rounded-none bg-white border border-zinc-200/90 shadow-md space-y-2">
              <h3 className="text-lg font-serif font-medium text-[#141416] flex items-center gap-3">
                <span className="text-black text-lg font-bold font-serif">1.</span>
                Tøjvalg og farvevalg
              </h3>
              <p className="text-[#575450] text-sm leading-relaxed">
                Vælg gerne rolige, afdæmpede jordfarver (sand, beige, råhvid, grå, dæmpet blå eller olivengrøn) og undgå store prints og skrigende logoer. I behøver ikke matche 100%, men det er smukt, hvis nuancerne harmonerer.
              </p>
            </div>

            <div className="p-6 sm:p-8 rounded-none bg-white border border-zinc-200/90 shadow-md space-y-2">
              <h3 className="text-lg font-serif font-medium text-[#141416] flex items-center gap-3">
                <span className="text-black text-lg font-bold font-serif">2.</span>
                Tidspunkt på dagen & det gode lys
              </h3>
              <p className="text-[#575450] text-sm leading-relaxed">
                Det smukkeste bløde lys findes om morgenen eller sent på eftermiddagen i den gyldne time. Men med mindre børn tilpasser vi os altid deres lure og energitidspunkter, så de er glade og veludhvilede.
              </p>
            </div>

            <div className="p-6 sm:p-8 rounded-none bg-white border border-zinc-200/90 shadow-md space-y-2">
              <h3 className="text-lg font-serif font-medium text-[#141416] flex items-center gap-3">
                <span className="text-black text-lg font-bold font-serif">3.</span>
                Tag det helt roligt – ingen stress
              </h3>
              <p className="text-[#575450] text-sm leading-relaxed">
                Hvis børnene er generte, krudtglade eller vilde, er det helt okay. Jeg tager mig god tid, og der er altid plads til pauser, mellemmåltider og krammere. De mest levende billeder opstår, når børnene glemmer kameraet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials from families */}
      <TestimonialsSection
        category="familie"
        title="Anbefalinger fra familier"
        subtitle="Mød de forældre og familier, der har haft Michael med til deres store dage"
        theme="light"
      />

      {/* CTA Section (Cinematic Dark) */}
      <section className="py-20 bg-[#0C0C0D] border-t border-white/10 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-8 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-serif text-[#F4EFEA] max-w-3xl mx-auto">
            Skal vi fange jeres families øjeblikke?
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Kontakt Michael for at booke en tid eller høre nærmere om mulighederne i Kolding og resten af landet.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt?ydelse=familie"
              className="cta-btn inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Book fotografering
            </Link>
            <a
              href="tel:26368306"
              className="cta-btn inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span className="whitespace-nowrap">26 36 83 06</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
